import { Component, ElementRef, HostListener, ViewChild } from '@angular/core'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTable, MatTableDataSource } from '@angular/material/table'
import { DiscountService } from '../discount.service'
import { VendorService } from 'src/app/multivendor/vendor.service'
import { Router } from '@angular/router'
import { AddonsAccessService } from 'src/app/addons/addons-subscribtion/addons-access.service'
import { MatDialog } from '@angular/material/dialog'
import { LimitModalComponent } from 'src/app/free-plan/limit-modal/limit-modal.component'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { environment } from 'src/environments/environment'

export interface Discount {
  id: string | null
  name: string
  amount: number
  discountCriteria: 'fixed_products_discount' | 'fixed_categories_discount'
}

@Component({
  selector: 'app-discount-list',
  templateUrl: './discount-list.component.html',
  styleUrl: './discount-list.component.scss'
})
export class DiscountListComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator
  @ViewChild(MatSort) matSort!: MatSort
  @ViewChild(MatTable) table!: MatTable<Discount>
  @ViewChild('searchInput') searchInput!: ElementRef
  displayedColumns: string[] = ['name', 'amount', 'sale_date_from', 'sale_date_to', 'discountCriteria', 'state', 'actions']
  endTime: any
  discountState: any
  dataSource = new MatTableDataSource<Discount>()
  searchTerm: string = ''
  discountCriteriaMap: { [key: string]: string } = {
    fixed_products_discount: 'Fixed Products Discount',
    fixed_categories_discount: 'Fixed Category Discount',
    whole_cart_discount: 'Whole Cart Discount'
  }
  // Grace period UI
  showGracePeriodWarning = false
  gracePeriodInfo: any = null
  addonName = 'Timer and Flash Sale'

  discountStates: { [key: string]: string } = {} // To store discount states
  pageIndex = 0
  pageSize = 10
  showFilterDropdown = false
  selectedStateFilter = 'all'

  // Delete modal state
  showDeleteModal = false
  discountToDeleteId: string | null = null
  discountToDeleteName: string = ''

  constructor(private discountService: DiscountService, private vendor: VendorService, private addonsAccess: AddonsAccessService, public router: Router, private dialog: MatDialog, public builderService: BuilderService) {}

  //valid not vaild
  ngOnInit() {
    this.addonsAccess.loadSubscriptions()
    this.addonsAccess.getSubscriptions$().subscribe(() => {
      this.checkGracePeriod()
    })
    this.loadDiscounts()
  }

  checkGracePeriod() {
    if (this.addonsAccess.isAddonInGracePeriod(this.addonName)) {
      this.gracePeriodInfo = this.addonsAccess.getGracePeriodInfo(this.addonName)
      this.showGracePeriodWarning = true
    } else {
      this.showGracePeriodWarning = false
    }
  }

validateDiscount() {
    if (this.addonsAccess.isAddonActive('All Addons Bundle') || this.addonsAccess.isAddonActive('Timer and Flash Sale')) {
      this.router.navigate(['/discount/add-discount'])
      return
    }

    this.openLockedModal()
  }

  openLockedModal() {
    const dialogRef = this.dialog.open(LimitModalComponent, {
      width: '337px',
      maxWidth: '90vw',
      panelClass: 'limit-dialog',
      backdropClass: 'blur-backdrop',
      hasBackdrop: true,
      disableClose: false,
      autoFocus: false,
      data: {
        title: 'Unlock Discounts',
        message: 'Discounts & Flash Sales is locked on your plan.',
        cta: 'Unlock Discounts'
      }
    })

    dialogRef.afterClosed().subscribe((upgrade) => {
      if (upgrade) {
        const token = localStorage.getItem('token')
        const params = new URLSearchParams()
        if (token) params.set('token', token)
        params.set('project_id', encodeURIComponent(this.builderService.selectedProject))
        window.open(`${environment.stacksURL}addons/?${params.toString()}`, '_blank')
      }
    })
  }

  /**
   * Fetches discounts from the server and populates the Material table with the data.
   * It also initializes the discount timer state for each discount.
   */
  loadDiscounts() {
    this.discountService.getDiscounts().subscribe(
      (discountsArray: any[]) => {
        setTimeout(() => {
          discountsArray.forEach((discount: any) => {
            this.discountTimerState(discount.id, discount.dateTo, discount.timeTo)
          })
          this.dataSource = new MatTableDataSource<Discount>(discountsArray)
          this.pageIndex = 0
        }, 1000)
      },
      (error) => {
        console.error('Error fetching discounts:', error)
      }
    )
  }

  /**
   * Applies a filter to the Material table data source based on the given filter value.
   * It trims and converts the filter value to lowercase before applying it.
   * If a paginator is present, it sets the first page as the current page after applying the filter.
   * @param filterValue - The value to filter the table data by.
   */
  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase()
    this.pageIndex = 0
  }

  toggleFilterDropdown(event: Event): void {
    event.stopPropagation()
    this.showFilterDropdown = !this.showFilterDropdown
  }

  selectFilter(filter: string): void {
    this.selectedStateFilter = filter
    this.showFilterDropdown = false
    this.pageIndex = 0
  }

  getFilterLabel(): string {
    const labels: { [key: string]: string } = {
      all: 'All Discounts',
      Active: 'Active',
      Expired: 'Expired'
    }
    return labels[this.selectedStateFilter] || 'All Discounts'
  }

  @HostListener('document:click')
  closeFilterDropdown(): void {
    this.showFilterDropdown = false
  }

  private get stateFilteredDiscounts(): Discount[] {
    if (this.selectedStateFilter === 'all') return this.dataSource.filteredData
    return this.dataSource.filteredData.filter((d: any) => this.discountStates[d.id] === this.selectedStateFilter)
  }

  get displayedDiscounts(): Discount[] {
    const start = this.pageIndex * this.pageSize
    return this.stateFilteredDiscounts.slice(start, start + this.pageSize)
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.stateFilteredDiscounts.length / this.pageSize))
  }

  previousPage(): void {
    if (this.pageIndex > 0) this.pageIndex--
  }

  nextPage(): void {
    if (this.pageIndex < this.getTotalPages() - 1) this.pageIndex++
  }

  /**
   * Deletes a discount timer from the DB.
   * Displays a confirmation dialog before proceeding with the deletion.
   * If the user confirms the deletion, it sends a request to the server to delete the discount timer.
   * On success, it logs a success message and refreshes the discount list.
   * On error, it logs an error message.
   *
   * @param discountId - The unique identifier of the discount timer to be deleted.
   */
  openDeleteModal(discountId: string, discountName: string) {
    this.discountToDeleteId = discountId
    this.discountToDeleteName = discountName
    this.showDeleteModal = true
  }

  closeDeleteModal() {
    this.showDeleteModal = false
    this.discountToDeleteId = null
    this.discountToDeleteName = ''
  }

  confirmDeleteDiscount() {
    if (!this.discountToDeleteId) return
    this.discountService.deleteDiscount(this.discountToDeleteId).subscribe(
      () => {
        this.loadDiscounts()
        this.closeDeleteModal()
      },
      (error) => {
        console.log('Error deleting discount:', error)
        this.closeDeleteModal()
      }
    )
  }

  /**
   * Checks if there are any discounts available in the table data source.
   * @returns {boolean} - Returns true if there are discounts available, false otherwise.
   */
  hasdiscounts(): boolean {
    return this.dataSource.data.length > 0
  }

  /**
   * Updates the state of a discount timer based on the given end date and time.
   * The function calculates the remaining time until the end date and updates the state accordingly.
   * If the end date is invalid, it logs an error message and sets the state to "Invalid Date".
   * The function is called every second using setInterval.
   * @param id - The unique identifier of the discount timer.
   * @param dateTo - The end date of the discount timer.
   * @param timeTo - The end time of the discount timer.
   * @returns {void}
   */
  discountTimerState(id: string, dateTo: any, timeTo: any): void {
    const concTime = `${dateTo} ${timeTo}`
    const endTime = new Date(concTime).getTime() // Convert to timestamp in milliseconds

    if (isNaN(endTime)) {
      console.error('Invalid Date Format:', concTime)
      this.discountStates[id] = 'Invalid Date'
      return
    }

    setInterval(() => {
      const now = new Date().getTime()
      const remainingTime = endTime - now

      if (remainingTime <= 0) {
        this.discountStates[id] = 'Expired'
      } else {
        this.discountStates[id] = 'Active'
      }
    }, 1000) // Updates every second
  }
}
